import { FC, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import { Form, Select } from 'antd';
import styled from 'styled-components';

import { ButtonIw } from '@/_pages/interwill/components/ButtonIw';
import { FieldContainerIw as FieldContainerIwBase } from '@/_pages/interwill/components/FieldContainerIw';
import { ButtonIwTextStyled } from '@/_pages/interwill/components/ParticipantContent/styled';
import { useI18nValues } from '@/entities/personalIdentifiers/hooks/useI18nValues';
import { isNationalId } from '@/features/sowe-create-appendix-modal/_component/summary/guards/is-national-id';
import { isPassport } from '@/features/sowe-create-appendix-modal/_component/summary/guards/is-passport';
import { Input } from '@/shared/components';
import { SelectCountry } from '@/shared/components/forms/fields/SelectCountry';
import { SelectGender } from '@/shared/components/forms/fields/SelectGender';
import { COLORS } from '@/shared/constants/colors';
import { useSJDatabase } from '@/shared/lib/sj-orm/hooks/use-sj-database';
import { DocumentType } from '@/shared/lib/sj-orm/models/document/document-base.dto';
import { PersonalIdentifierType } from '@/shared/lib/sj-orm/models/document/personal-identifier.document.dto';
import { PersonalIdentifierDocumentDto } from '@/shared/lib/sj-orm/models/personalIdentifiersDocuments/personalIdentifiersDocuments.dto';
import { DatePicker } from '@/shared/uikit/DatePicker';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { DocumentDetailsModalProps } from '@/widgets/modals/types';

import { ModalContainerV1 } from '../containers/ModalContainerV1';
import { InterwillStyledForm } from '../CreateEntityModal/styled';

import { DocumentDetailsParams } from './types';

export const DocumentDetailsModal: FC<DocumentDetailsModalProps> = ({
  pidId,
  beneficiaryData,
  updateBeneficiaryData,
}) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'documentDetails' });
  const { t: tf } = useTranslation('widgets/modals', { keyPrefix: 'createPid' });

  const sjDatabase = useSJDatabase();
  const modals = useModalsStore();

  const { type } = useI18nValues();

  const pidData = useMemo(() => {
    const data = sjDatabase.collections.personalIdentifiersDocuments?.findById(pidId);

    if (data && (isPassport(data) || isNationalId(data))) return data;
    return undefined;
  }, [pidId]);

  const {
    control,
    handleSubmit: formHandleSubmit,
    formState: { isSubmitting },
    watch,
  } = useForm<DocumentDetailsParams>({
    defaultValues: {
      id: pidData?.id,
      country: pidData?.country,
      documentNumber: pidData?.number,
      idType: pidData?.personalIdentifierType,
      startDate: pidData?.dateOfIssue,
      firstName: pidData?.names,
      lastName: pidData?.surnames,
      secondName: pidData?.secondName,
      gender: pidData?.gender,
      dateOfBirth: pidData?.birthDate,
    },
  });

  const onSubmitHandler = async (values: DocumentDetailsParams) => {
    const newDto: PersonalIdentifierDocumentDto = {
      ...pidData,
      id: values.id,
      name: values.name,
      type: DocumentType.PERSONAL_IDENTIFIERS,
      personalIdentifierType: values.idType,
      dateOfIssue: values.startDate,
      number: values.documentNumber,
      country: values.country,
      beneficiaryId: 'me',
      names: values.firstName,
      surnames: values.lastName,
      secondName: values?.secondName,
      gender: values?.gender,
      birthDate: values?.dateOfBirth,
    };

    await sjDatabase.collections.personalIdentifiersDocuments?.update(newDto);
    updateBeneficiaryData({
      firstName: values.firstName,
      lastName: values.lastName,
      secondName: values.secondName,
      birthDate: values.dateOfBirth,
      gender: values.gender,
      primaryCitizenship: {
        id: values.id,
        country: values.country,
        documentType: values.idType,
        number: values.documentNumber,
        issueDate: values.startDate,
      },
    });
    modals.closeModal();
  };
  const isPidPassport = watch('idType') === PersonalIdentifierType.PASSPORT;

  return (
    <ModalContainerV1 title={t('title')}>
      <InterwillStyledForm onFinish={formHandleSubmit(onSubmitHandler)}>
        <InnerForm>
          <Controller
            name={'idType'}
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState }) => {
              return (
                <FieldContainerIwDisabled label={tf(`fields.${field.name}.label`)} required>
                  <Form.Item
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <Select
                      placeholder={tf(`fields.${field.name}.placeholder`)}
                      {...field}
                      options={[
                        PersonalIdentifierType.PASSPORT,
                        PersonalIdentifierType.NATIONAL_ID,
                      ].map((item) => ({
                        label: type(item),
                        value: item,
                      }))}
                      disabled
                    />
                  </Form.Item>
                </FieldContainerIwDisabled>
              );
            }}
          />

          <ButtonIwTextStyled
            onClick={() => {
              modals.closeModal();
              modals.openModal({
                type: 'SelectCitizenshipModal',
                beneficiaryData,
                updateBeneficiaryData,
              });
            }}
          >
            {t('replaceBtn')}
          </ButtonIwTextStyled>

          <Separator />

          <Controller
            name={'name'}
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState }) => {
              return (
                <FieldContainerIw label={tf(`fields.${field.name}.label`)} required>
                  <Form.Item
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <SelectCountry
                      placeholder={tf(`fields.${field.name}.placeholder`)}
                      {...field}
                    />
                  </Form.Item>
                </FieldContainerIw>
              );
            }}
          />

          <Controller
            name={'country'}
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState }) => {
              return (
                <FieldContainerIw label={tf(`fields.${field.name}.label`)} required>
                  <Form.Item
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <SelectCountry
                      placeholder={tf(`fields.${field.name}.placeholder`)}
                      {...field}
                    />
                  </Form.Item>
                </FieldContainerIw>
              );
            }}
          />

          <Controller
            name={'documentNumber'}
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState }) => {
              return (
                <FieldContainerIw
                  label={tf(`fields.${isPidPassport ? 'passportNumber' : 'documentNumber'}.label`)}
                  required
                >
                  <Form.Item help={fieldState.error?.message}>
                    <Input
                      placeholder={tf(
                        `fields.${isPidPassport ? 'passportNumber' : 'documentNumber'}.placeholder`,
                      )}
                      type={'text'}
                      {...field}
                    />
                  </Form.Item>
                </FieldContainerIw>
              );
            }}
          />

          <Controller
            name={'startDate'}
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState }) => {
              return (
                <FieldContainerIw required label={tf(`fields.${field.name}.label`)}>
                  <Form.Item help={fieldState.error?.message}>
                    <DatePicker
                      format={'DD MMMM YYYY'}
                      placeholder={tf(`fields.${field.name}.placeholder`)}
                      {...field}
                    />
                  </Form.Item>
                </FieldContainerIw>
              );
            }}
          />

          <Row>
            <Controller
              name={'firstName'}
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => {
                return (
                  <FieldContainerIw label={tf(`fields.${field.name}.label`)} required>
                    <Form.Item
                      validateStatus={fieldState.error ? 'error' : ''}
                      help={fieldState.error?.message}
                    >
                      <Input
                        placeholder={tf(`fields.${field.name}.placeholder`)}
                        type={'text'}
                        {...field}
                      />
                    </Form.Item>
                  </FieldContainerIw>
                );
              }}
            />
            <Controller
              name={'lastName'}
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => {
                return (
                  <FieldContainerIw label={tf(`fields.${field.name}.label`)} required>
                    <Form.Item
                      validateStatus={fieldState.error ? 'error' : ''}
                      help={fieldState.error?.message}
                    >
                      <Input
                        placeholder={tf(`fields.${field.name}.placeholder`)}
                        type={'text'}
                        {...field}
                      />
                    </Form.Item>
                  </FieldContainerIw>
                );
              }}
            />
          </Row>
          <Row>
            <Controller
              name={'secondName'}
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <FieldContainerIw label={tf(`fields.${field.name}.label`)}>
                    <Form.Item help={fieldState.error?.message}>
                      <Input
                        placeholder={tf(`fields.${field.name}.placeholder`)}
                        type={'text'}
                        {...field}
                      />
                    </Form.Item>
                  </FieldContainerIw>
                );
              }}
            />
          </Row>
          <Row>
            <Controller
              name={'dateOfBirth'}
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => {
                return (
                  <FieldContainerIw label={tf(`fields.${field.name}.label`)} required>
                    <Form.Item
                      validateStatus={fieldState.error ? 'error' : ''}
                      help={fieldState.error?.message}
                    >
                      <DatePicker
                        format={'DD MMMM YYYY'}
                        placeholder={tf(`fields.${field.name}.placeholder`)}
                        {...field}
                      />
                    </Form.Item>
                  </FieldContainerIw>
                );
              }}
            />
            <Controller
              name={'gender'}
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => {
                return (
                  <FieldContainerIw label={tf(`fields.${field.name}.label`)} required>
                    <Form.Item
                      validateStatus={fieldState.error ? 'error' : ''}
                      help={fieldState.error?.message}
                    >
                      <SelectGender
                        placeholder={tf(`fields.${field.name}.placeholder`)}
                        {...field}
                      />
                    </Form.Item>
                  </FieldContainerIw>
                );
              }}
            />
          </Row>
        </InnerForm>
        <Buttons>
          <ButtonIw loading={isSubmitting} disabled={isSubmitting} htmlType="submit">
            {t('saveBtn')}
          </ButtonIw>
        </Buttons>
      </InterwillStyledForm>
    </ModalContainerV1>
  );
};

const InnerForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 420px;
  overflow: auto;
  padding: 0 2px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 40px;
`;

const FieldContainerIw = styled(FieldContainerIwBase)`
  flex: 0 50%;
`;

const Separator = styled.div`
  width: 100%;
  height: 1px;
  background: ${COLORS.systemDark100};
  flex-shrink: 0;
`;

const FieldContainerIwDisabled = styled(FieldContainerIw)`
  color: ${COLORS.systemDark50A};
`;
